import React, { useCallback, useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import {
  DndContext,
  DragEndEvent,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { GripVertical, Trash2Icon } from "lucide-react";
import ThumbnailConstraintWarning from "@/components/ThumbnailConstraintWarning/ThumbnailConstraintWarning";
import FilePickerDialog, {
  FilePickerSelectedFile,
} from "@/modules/hub/features/FileManagement/components/FilePickerDialog/FilePickerDialog";
import ThumbnailDisplay from "@/modules/hub/features/FileManagement/components/ThumbnailDisplay/ThumbnailDisplay";
import { CourseFormData } from "@/modules/learningCenter/features/CourseManagement/components/CourseForm/types";
import {
  Course,
  CourseMetadata,
} from "@/modules/learningCenter/features/CourseManagement/types/course";

const NONE_VALUE = "__none__";

interface CourseDetailsFieldsProps {
  form: UseFormReturn<CourseFormData>;
  metadata: CourseMetadata | null;
  /** Edit-mode only: the loaded course (for existing media URLs). */
  course?: Course | null;
}

/** A bordered, boxed section with a labeled header. */
function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 space-y-4">
      <div>
        <h2 className="text-base font-normal text-gray-900 dark:text-white">
          {title}
        </h2>
        {description && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {description}
          </p>
        )}
      </div>
      {children}
    </section>
  );
}

interface SortableObjectiveProps {
  id: string;
  index: number;
  value: string;
  onChange: (value: string) => void;
  onRemove: () => void;
}

function SortableObjective({
  id,
  index,
  value,
  onChange,
  onRemove,
}: SortableObjectiveProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div className="flex items-center gap-2" ref={setNodeRef} style={style}>
      <button
        aria-label={`Reorder objective ${index + 1}`}
        className="cursor-grab text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        type="button"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <Input
        aria-label={`Learning objective ${index + 1}`}
        onChange={(e) => onChange(e.target.value)}
        placeholder={`Objective ${index + 1}`}
        value={value}
      />
      <button
        aria-label={`Remove objective ${index + 1}`}
        className="text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
        onClick={onRemove}
        type="button"
      >
        <Trash2Icon className="h-4 w-4" />
      </button>
    </div>
  );
}

/**
 * The shared course-detail authoring surface, broken into bordered, boxed
 * labeled sections. Reused by the Create screen and the Edit screen's
 * "Course Details" pane. Module management is intentionally NOT part of this
 * component — that lives only on the Edit screen.
 */
export function CourseDetailsFields({
  form,
  metadata,
  course,
}: CourseDetailsFieldsProps) {
  const { control, setValue, watch } = form;

  const [isThumbnailPickerOpen, setThumbnailPickerOpen] = useState(false);
  const [isHeroPickerOpen, setHeroPickerOpen] = useState(false);
  const [thumbnailDisplayUrl, setThumbnailDisplayUrl] = useState<string>(
    course?.thumbnailUrl ?? "",
  );
  const [heroDisplayUrl, setHeroDisplayUrl] = useState<string>(
    course?.heroBannerUrl ?? "",
  );

  // On the edit screen the course fetch resolves AFTER this component mounts
  // (course is null on first render), so the useState seeds above capture empty
  // strings. Re-sync the previews when the fetched course's images change —
  // this also refreshes them after a save re-fetch. Only depends on the server
  // URLs, so it never clobbers an in-progress upload before saving.
  useEffect(() => {
    setThumbnailDisplayUrl(course?.thumbnailUrl ?? "");
    setHeroDisplayUrl(course?.heroBannerUrl ?? "");
  }, [course?.thumbnailUrl, course?.heroBannerUrl]);

  const learningObjectives = watch("learningObjectives");
  const tradeIds = watch("tradeIds");

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleThumbnailSelected = useCallback(
    (files: FilePickerSelectedFile[]) => {
      if (files.length === 0) return;
      const file = files[0];
      setThumbnailDisplayUrl(file.publicUrl);
      setValue("thumbnailFileUuid", file.fileUuid, { shouldDirty: true });
      setThumbnailPickerOpen(false);
    },
    [setValue],
  );

  const handleHeroSelected = useCallback(
    (files: FilePickerSelectedFile[]) => {
      if (files.length === 0) return;
      const file = files[0];
      setHeroDisplayUrl(file.publicUrl);
      setValue("heroBannerFileUuid", file.fileUuid, { shouldDirty: true });
      setHeroPickerOpen(false);
    },
    [setValue],
  );

  const handleAddObjective = useCallback(() => {
    setValue("learningObjectives", [...learningObjectives, ""], {
      shouldDirty: true,
    });
  }, [learningObjectives, setValue]);

  const handleObjectiveChange = useCallback(
    (index: number, value: string) => {
      const next = [...learningObjectives];
      next[index] = value;
      setValue("learningObjectives", next, { shouldDirty: true });
    },
    [learningObjectives, setValue],
  );

  const handleRemoveObjective = useCallback(
    (index: number) => {
      setValue(
        "learningObjectives",
        learningObjectives.filter((_, i) => i !== index),
        { shouldDirty: true },
      );
    },
    [learningObjectives, setValue],
  );

  const handleObjectiveDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = Number(active.id);
      const newIndex = Number(over.id);
      if (Number.isNaN(oldIndex) || Number.isNaN(newIndex)) return;
      setValue(
        "learningObjectives",
        arrayMove(learningObjectives, oldIndex, newIndex),
        { shouldDirty: true },
      );
    },
    [learningObjectives, setValue],
  );

  const trades = metadata?.trades ?? [];
  const levels = metadata?.levels ?? [];
  const allTradesSelected =
    trades.length > 0 && tradeIds.length === trades.length;

  const handleToggleTrade = useCallback(
    (id: string) => {
      const next = tradeIds.includes(id)
        ? tradeIds.filter((t) => t !== id)
        : [...tradeIds, id];
      setValue("tradeIds", next, { shouldDirty: true });
    },
    [tradeIds, setValue],
  );

  const handleToggleAllTrades = useCallback(() => {
    setValue(
      "tradeIds",
      allTradesSelected ? [] : trades.map((t) => String(t.id)),
      { shouldDirty: true },
    );
  }, [allTradesSelected, trades, setValue]);

  return (
    <Form {...form}>
      <div className="space-y-6">
        {/* Course Details */}
        <Section title="Course Details">
          <FormField
            control={control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Title</FormLabel>
                <FormControl>
                  <Input placeholder="Course title" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="tagline"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tagline</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Short tagline"
                    {...field}
                    value={field.value ?? ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Course description"
                    {...field}
                    value={field.value ?? ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="courseOverview"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Course overview</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="A longer overview of the course"
                    {...field}
                    value={field.value ?? ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </Section>

        {/* Course At a Glance */}
        <Section title="Course At a Glance">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={control}
              name="targetAudience"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Target audience</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Who is this course for?"
                      {...field}
                      value={field.value ?? ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="level"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Level</FormLabel>
                  <FormControl>
                    <Select
                      onValueChange={(val) =>
                        field.onChange(val === NONE_VALUE ? null : val)
                      }
                      value={field.value || NONE_VALUE}
                    >
                      <SelectTrigger aria-label="Level">
                        <SelectValue placeholder="Select level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE_VALUE}>None</SelectItem>
                        {levels.map((level) => (
                          <SelectItem key={level} value={level}>
                            {level}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </Section>

        {/* Learning Objectives */}
        <Section title="Learning Objectives">
          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={handleObjectiveDragEnd}
            sensors={sensors}
          >
            <SortableContext
              items={learningObjectives.map((_, i) => String(i))}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {learningObjectives.map((objective, index) => (
                  <SortableObjective
                    id={String(index)}
                    index={index}
                    key={index}
                    onChange={(value) => handleObjectiveChange(index, value)}
                    onRemove={() => handleRemoveObjective(index)}
                    value={objective}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
          <Button
            onClick={handleAddObjective}
            size="sm"
            type="button"
            variant="outline"
          >
            + Add Objective
          </Button>
        </Section>

        {/* Estimated Hours */}
        <Section title="Estimated Hours">
          <FormField
            control={control}
            name="estimatedHours"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Estimated hours</FormLabel>
                <FormControl>
                  <Input
                    placeholder="e.g. 4"
                    {...field}
                    value={field.value ?? ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </Section>

        {/* Trade Availability */}
        <Section
          description="Select which trades can access this course."
          title="Trade Availability"
        >
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {tradeIds.length} of {trades.length} selected
            </span>
            <Button
              onClick={handleToggleAllTrades}
              size="sm"
              type="button"
              variant="ghost"
            >
              {allTradesSelected ? "Deselect All" : "Select All"}
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {trades.map((trade) => {
              const id = String(trade.id);
              const checked = tradeIds.includes(id);
              return (
                <label
                  className="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-slate-700 p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
                  key={trade.id}
                >
                  <Checkbox
                    aria-label={trade.name}
                    checked={checked}
                    onCheckedChange={() => handleToggleTrade(id)}
                  />
                  <span className="text-sm text-gray-900 dark:text-white">
                    {trade.name}
                  </span>
                </label>
              );
            })}
          </div>
        </Section>

        {/* Media */}
        <Section title="Media">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <FormLabel>Thumbnail</FormLabel>
              <FormDescription>
                Recommended 16:9, at least 1280x720.
              </FormDescription>
              <div className="mt-2">
                <ThumbnailDisplay
                  loading={false}
                  onRemove={() => {
                    setThumbnailDisplayUrl("");
                    setValue("thumbnailFileUuid", null, { shouldDirty: true });
                  }}
                  onSelect={() => setThumbnailPickerOpen(true)}
                  thumbnailUrl={thumbnailDisplayUrl}
                />
                <ThumbnailConstraintWarning url={thumbnailDisplayUrl} />
              </div>
            </div>

            <div>
              <FormLabel>Hero banner</FormLabel>
              <FormDescription>
                Recommended 16:9, at least 1280x720.
              </FormDescription>
              <div className="mt-2">
                <ThumbnailDisplay
                  label="hero banner"
                  loading={false}
                  onRemove={() => {
                    setHeroDisplayUrl("");
                    setValue("heroBannerFileUuid", null, { shouldDirty: true });
                  }}
                  onSelect={() => setHeroPickerOpen(true)}
                  thumbnailUrl={heroDisplayUrl}
                />
                <ThumbnailConstraintWarning
                  label="Hero banners"
                  url={heroDisplayUrl}
                />
              </div>
            </div>
          </div>
        </Section>
      </div>

      <FilePickerDialog
        allowedFileTypes={["image/jpeg", "image/png"]}
        isOpen={isThumbnailPickerOpen}
        onClose={() => setThumbnailPickerOpen(false)}
        onSelect={handleThumbnailSelected}
        title="Select thumbnail"
      />

      <FilePickerDialog
        allowedFileTypes={["image/jpeg", "image/png"]}
        isOpen={isHeroPickerOpen}
        onClose={() => setHeroPickerOpen(false)}
        onSelect={handleHeroSelected}
        title="Select hero banner"
      />
    </Form>
  );
}
